import React, { useState, useEffect } from 'react';
import { getAllAdmins, updateAdminStatus, deleteAdmin, generateDailyInviteCode } from '../services/firestoreService';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';
import '../styles/AdminManagement.css';

const AdminManagement = ({ user }) => {
  const { alertState, showSuccess, showError, showWarning, showConfirm } = useCustomAlert();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeLoading, setInviteCodeLoading] = useState(false);

  // 관리자 목록 로드
  useEffect(() => {
    loadAdmins();
    loadInviteCode();
  }, []);

  const loadInviteCode = async () => {
    try {
      setInviteCodeLoading(true);
      const code = await generateDailyInviteCode();
      setInviteCode(code);
    } catch (error) {
      console.error('초대코드 로드 오류:', error);
    } finally {
      setInviteCodeLoading(false);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    showSuccess('초대코드가 클립보드에 복사되었습니다!');
  };

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const adminList = await getAllAdmins();
      setAdmins(adminList);
    } catch (error) {
      console.error('관리자 목록 로드 오류:', error);
      await showError('관리자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };



  // 관리자 삭제
  const handleDeleteAdmin = async (adminId, adminName) => {
    const confirmed = await showConfirm(
      `정말로 "${adminName}" 관리자를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`,
      '관리자 삭제 확인',
      '삭제하기',
      '취소'
    );
    
    if (!confirmed) return;

    try {
      await deleteAdmin(adminId);
      await showSuccess('관리자가 삭제되었습니다.');
      
      // 현재 사용자가 삭제된 경우 로그아웃 처리
      if (adminId === user?.id) {
        await showWarning('자신의 계정이 삭제되어 로그아웃됩니다.');
        window.location.href = '/';
      } else {
        loadAdmins(); // 관리자 목록 새로고침
      }
    } catch (error) {
      console.error('관리자 삭제 오류:', error);
      showError('관리자 삭제 중 오류가 발생했습니다.');
    }
  };

  // 필터링 및 정렬
  const filteredAndSortedAdmins = admins
    .filter(admin => 
      admin.name.toLowerCase().includes(filterText.toLowerCase()) ||
      admin.email.toLowerCase().includes(filterText.toLowerCase())
    )
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'createdAt') {
        aValue = new Date(aValue?.toDate?.() || aValue);
        bValue = new Date(bValue?.toDate?.() || bValue);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // 정렬 변경
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };



  return (
    <div className="admin-management-container">
      <div className="page-header">
        <h1>👨‍💼 관리자 계정 관리</h1>
        <p>시스템의 모든 관리자 계정을 관리합니다.</p>
      </div>

      <div className="management-content">
        {/* 초대코드 섹션 */}
        <div className="invite-code-section">
          <div className="invite-code-card">
            <h3>🎫 오늘의 초대코드</h3>
            <div className="invite-code-content">
              {inviteCodeLoading ? (
                <p>로딩 중...</p>
              ) : (
                <>
                  <div className="invite-code-display">
                    <span className="invite-code">{inviteCode}</span>
                    <button onClick={copyInviteCode} className="copy-button">
                      📋 복사
                    </button>
                  </div>
                  <p className="invite-code-info">
                    이 코드를 새로운 관리자에게 공유하세요.<br/>
                  관리자 최초 로그인시 사용됩니다.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 필터 및 정렬 */}
        <div className="controls-section">
          <div className="search-filter">
            <input
              type="text"
              placeholder="이름 또는 이메일로 검색..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="sort-controls">
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="sort-select"
            >
              <option value="name-asc">이름 (오름차순)</option>
              <option value="name-desc">이름 (내림차순)</option>
              <option value="email-asc">이메일 (오름차순)</option>
              <option value="email-desc">이메일 (내림차순)</option>
              <option value="createdAt-desc">최근 생성순</option>
              <option value="createdAt-asc">오래된 순</option>
            </select>
          </div>
        </div>

        {/* 관리자 목록 */}
        <div className="admins-section">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>관리자 목록을 불러오는 중...</p>
            </div>
          ) : filteredAndSortedAdmins.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>관리자가 없습니다</h3>
              <p>
                {filterText ? '검색 결과가 없습니다.' : '등록된 관리자가 없습니다.'}
              </p>
            </div>
          ) : (
            <div className="admins-grid">
              {filteredAndSortedAdmins.map((admin) => (
                <div key={admin.id} className="admin-card">
                  <div className="admin-header">
                    <div className="admin-avatar">
                      {admin.name.charAt(0)}
                    </div>
                    <div className="admin-info">
                      <h3 className="admin-name">{admin.name}</h3>
                      <p className="admin-email">{admin.email}</p>
                    </div>

                  </div>

                  <div className="admin-details">
                    <div className="detail-item">
                      <span className="detail-label">생성일:</span>
                      <span className="detail-value">
                        {admin.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">마지막 수정:</span>
                      <span className="detail-value">
                        {admin.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className="admin-actions">
                                         <button
                       className="delete-btn"
                       onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                     >
                      삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 통계 정보 */}
        <div className="stats-section">
          <div className="stats-card">
            <h3>📊 관리자 통계</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{admins.length}</span>
                <span className="stat-label">전체 관리자</span>
              </div>

            </div>
          </div>
        </div>
      </div>

      <CustomAlert {...alertState} />
    </div>
  );
};

export default AdminManagement; 